import React, { useState, useRef } from 'react';
import Popover from '@mui/material/Popover';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { PopupOption } from '../Popup';
import './inputs.scss';

/**
 * FancySelect — looks like FancyTextField, behaves as a dropdown.
 *
 *   <FancySelect
 *     label="Category"
 *     value={categoryId}
 *     onChange={(value) => setCategoryId(value)}
 *     options={[{ value: 'a', label: 'Alpha', icon?: <Icon/>, subtitle?: '...' }, ...]}
 *     fullWidth
 *   />
 *
 * Each option: { value, label, icon?, subtitle?, danger? }
 */
export const FancySelect = ({
  label,
  value,
  onChange,
  options = [],
  placeholder = 'Select…',
  helperText,
  error = false,
  disabled = false,
  fullWidth = false,
  style,
  className = '',
  renderValue,
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const triggerRef = useRef(null);
  const open = Boolean(anchorEl);

  const handleToggle = () => {
    if (disabled) return;
    setAnchorEl(triggerRef.current);
  };

  const handleClose = () => setAnchorEl(null);

  const selectedOption = options.find((o) => o.value === value);
  const display = renderValue
    ? renderValue(value, selectedOption)
    : (selectedOption ? selectedOption.label : null);
  const filled = display != null && display !== '';
  const floating = open || filled;

  const classes = [
    'fancy-tf',
    'fancy-select',
    fullWidth ? 'fancy-tf--full' : '',
    error ? 'fancy-tf--error' : '',
    disabled ? 'fancy-tf--disabled' : '',
    open ? 'fancy-tf--focused fancy-select--open' : '',
    floating ? 'fancy-tf--floating' : '',
    label ? 'fancy-tf--has-label' : '',
    className,
  ].filter(Boolean).join(' ');

  return (
    <div ref={triggerRef} className={classes} style={style}>
      <div
        className="fancy-tf__field"
        onClick={handleToggle}
        role="button"
        tabIndex={disabled ? -1 : 0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
            e.preventDefault();
            handleToggle();
          }
        }}
      >
        <div className="fancy-tf__inner">
          {label && <label className="fancy-tf__label">{label}</label>}
          <div className={`fancy-select__value ${!filled ? 'fancy-select__value--placeholder' : ''}`}>
            {filled ? display : (floating ? placeholder : '')}
          </div>
        </div>
        <span className="fancy-tf__adornment fancy-tf__adornment--end">
          <KeyboardArrowDownIcon className="fancy-select__chevron" fontSize="small" />
        </span>
      </div>
      {helperText && (
        <div className={`fancy-tf__helper ${error ? 'fancy-tf__helper--error' : ''}`}>{helperText}</div>
      )}

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        className="fancy-select__menu"
        PaperProps={{
          style: {
            width: triggerRef.current ? triggerRef.current.offsetWidth : undefined,
          },
        }}
      >
        {options.map((opt) => (
          <PopupOption
            key={opt.value}
            icon={opt.icon}
            title={opt.label}
            subtitle={opt.subtitle}
            variant={opt.danger ? 'danger' : 'default'}
            onClick={() => {
              if (onChange) onChange(opt.value, opt);
              handleClose();
            }}
          />
        ))}
      </Popover>
    </div>
  );
};
